import { supabase } from "@/integrations/supabase/client";

export type ErrorType = 'auth' | 'project' | 'versioning' | 'sharing' | 'commenting' | 'general';
export type ErrorSeverity = 'critical' | 'error' | 'warning';

interface LogErrorOptions {
  errorType: ErrorType;
  severity?: ErrorSeverity;
  message: string;
  error?: Error | unknown;
  errorCode?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Centralized error logging service that logs errors to Supabase
 * and triggers alerts via the send-error-alert edge function for critical/error severity.
 */
export async function logError({
  errorType,
  severity = 'error',
  message,
  error,
  errorCode,
  metadata = {}
}: LogErrorOptions): Promise<void> {
  try {
    // Get current user if authenticated
    const { data: { user } } = await supabase.auth.getUser();
    
    // Extract stack trace if error is an Error object
    const stackTrace = error instanceof Error ? error.stack : undefined;
    
    // Extract error code from Supabase/Auth errors if not provided
    const finalErrorCode = errorCode || (error as { code?: string })?.code;
    
    // Log to database - cast to any since types may not be regenerated yet
    const { error: insertError } = await supabase
      .from('error_logs' as any)
      .insert({
        error_type: errorType,
        severity,
        message,
        stack_trace: stackTrace,
        error_code: finalErrorCode,
        user_id: user?.id || null,
        page_url: typeof window !== 'undefined' ? window.location.href : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        metadata
      } as any);

    if (insertError) {
      console.error('Failed to log error to database:', insertError);
    }

    // Also log to console for development
    console.error(`[${severity.toUpperCase()}] ${errorType}: ${message}`, error);

    // Trigger alert via edge function for critical/error severity
    if (severity === 'critical' || severity === 'error') {
      await triggerErrorAlert({
        errorType,
        severity,
        message,
        errorCode: finalErrorCode,
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        userId: user?.id,
        metadata
      });
    }
  } catch (e) {
    // Fail silently - don't let error logging break the app
    console.error('Error logger failed:', e);
  }
}

async function triggerErrorAlert(errorData: Record<string, unknown>): Promise<void> {
  try {
    await supabase.functions.invoke('send-error-alert', {
      body: errorData
    });
  } catch (e) {
    console.error('Failed to trigger error alert:', e);
  }
}

/**
 * Helper function for logging auth errors
 */
export function logAuthError(message: string, error?: Error | unknown, metadata?: Record<string, unknown>) {
  return logError({
    errorType: 'auth',
    severity: 'critical',
    message,
    error,
    metadata
  });
}

/**
 * Helper function for logging project errors
 */
export function logProjectError(message: string, error?: Error | unknown, metadata?: Record<string, unknown>) {
  return logError({
    errorType: 'project',
    severity: 'error',
    message,
    error,
    metadata
  });
}

/**
 * Helper function for logging versioning errors
 */
export function logVersioningError(message: string, error?: Error | unknown, metadata?: Record<string, unknown>) {
  return logError({
    errorType: 'versioning',
    severity: 'error',
    message,
    error,
    metadata
  });
}

/**
 * Helper function for logging sharing errors
 */
export function logSharingError(message: string, error?: Error | unknown, metadata?: Record<string, unknown>) {
  return logError({
    errorType: 'sharing',
    severity: 'error',
    message,
    error,
    metadata
  });
}

/**
 * Helper function for logging commenting errors
 */
export function logCommentingError(message: string, error?: Error | unknown, metadata?: Record<string, unknown>) {
  return logError({
    errorType: 'commenting',
    severity: 'error',
    message,
    error,
    metadata
  });
}
